"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { ChevronRight, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface PageHeroProps {
  icon: LucideIcon;
  overline: string;
  overlineClassName?: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  breadcrumbs?: { label: string; href?: string }[];
  badge?: React.ReactNode;
  pattern?: boolean;
}

export function PageHero({
  icon: Icon,
  overline,
  overlineClassName = "text-red",
  title,
  subtitle,
  breadcrumbs = [],
  badge,
  pattern = true,
}: PageHeroProps) {
  return (
    <section
      aria-label="Page Header"
      className="relative pt-24 pb-12 lg:pt-32 lg:pb-16 overflow-hidden"
    >
      {pattern && (
        <div aria-hidden className="absolute inset-0 bg-gradient-hero" />
      )}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04] [background-image:radial-gradient(circle_at_1px_1px,_currentColor_1px,_transparent_0)] [background-size:32px_32px] pointer-events-none"
      />
      <div aria-hidden className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-red/12 blur-3xl animate-fade-in" />
      <div aria-hidden className="absolute -bottom-24 -right-24 w-[420px] h-[420px] rounded-full bg-purple/10 blur-3xl animate-fade-in animation-delay-300" />

      <div className="container-1720 relative z-elevated">
        {breadcrumbs.length > 0 && (
          <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-1.5 text-caption text-muted-foreground">
            {breadcrumbs.map((b, i) => (
              <React.Fragment key={i}>
                {i > 0 && <ChevronRight className="size-3 opacity-50" />}
                {b.href ? (
                  <Link href={b.href} className="hover:text-red transition-colors">
                    {b.label}
                  </Link>
                ) : (
                  <span className="text-foreground">{b.label}</span>
                )}
              </React.Fragment>
            ))}
          </nav>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass border border-border/60 mb-5">
            <Icon className={cn("size-3.5", overlineClassName)} />
            <span className={cn("text-overline", overlineClassName)}>{overline}</span>
            {badge && (
              <>
                <span className="text-muted-foreground/50">·</span>
                <span className="text-overline text-muted-foreground">{badge}</span>
              </>
            )}
          </div>
          <h1 className="text-display-lg mb-5 leading-[1.05] tracking-tight">{title}</h1>
          {subtitle && (
            <p className="text-body-lg text-foreground/80 leading-relaxed max-w-3xl">
              {subtitle}
            </p>
          )}
        </motion.div>
      </div>
    </section>
  );
}
